import { useEffect } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  NuorderOrderingActionTypes,
  useNuorderOrdering,
} from 'root/apps/PurchaseOrder/NuorderOrdering/context/Context';
import {
  completeNuorderOrder,
  createNuorderOrder,
  deletePurchaseLine,
  fetchConfigs,
  fetchItems,
  fetchNuorderAvailability,
  fetchNuorderBrand,
  fetchNuorderOrder,
  fetchNuorderProductByUPC,
  fetchPurchase,
  fetchPurchaseLines,
  fetchSortingPreference,
  fetchVendor,
  fetchVendorShop,
  updateItem,
  updateItemVendorNum,
  updateNuorderOrder,
  updatePurchase,
  updatePurchaseLine,
  updateSortingPreference,
} from 'root/apps/PurchaseOrder/NuorderOrdering/context/Requests';
import {
  Item,
  ItemVendorNum,
  NuorderOrderRequest,
  PurchaseLine,
  PurchaseRequest,
  UpdateItemVendorNumRequest,
} from 'root/apps/PurchaseOrder/NuorderOrdering/types';

export const QueryKeys = {
  PURCHASE: 'PURCHASE',
  PURCHASE_LINES: 'PURCHASE_LINES',
  ITEMS: 'ITEMS',
  ITEM_VENDOR_NUM: 'ITEM_VENDOR_NUM',
  NUORDER_ORDER: 'NUORDER_ORDER',
  NUORDER_BRAND: 'NUORDER_BRAND',
  VENDOR: 'VENDOR',
  VENDOR_SHOP: 'VENDOR_SHOP',
  NUORDER_PRODUCTS: 'NUORDER_PRODUCTS',
  NUORDER_AVAILABILITY: 'NUORDER_AVAILABILITY',
  CONFIGS: 'CONFIGS',
  SORTING_PREFERENCE: 'SORTING_PREFERENCE',
};

const useGetEnabled = (enabled?: boolean): boolean | undefined => {
  const queryClient = useQueryClient();
  return enabled != null ? enabled : queryClient.getDefaultOptions().queries?.enabled;
};

export const useFetchPurchase = (purchaseID: number, enabled?: boolean) => {
  const { dispatch } = useNuorderOrdering();
  const query = useQuery({
    queryKey: [QueryKeys.PURCHASE, purchaseID],
    queryFn: () => fetchPurchase(purchaseID),
    enabled: useGetEnabled(enabled),
  });
  useEffect(() => {
    if (query.data) {
      dispatch({ type: NuorderOrderingActionTypes.SET_PURCHASE, payload: query.data });
    }
  }, [query.data, dispatch]);
  return query;
};

export const useFetchPurchaseLines = (purchaseID: number, enabled?: boolean) => {
  const { dispatch } = useNuorderOrdering();
  const query = useQuery({
    queryKey: [QueryKeys.PURCHASE_LINES, purchaseID],
    queryFn: () => fetchPurchaseLines(purchaseID),
    enabled: useGetEnabled(enabled),
  });
  useEffect(() => {
    if (query.data) {
      dispatch({ type: NuorderOrderingActionTypes.SET_PURCHASE_LINES, payload: query.data });
    }
  }, [query.data, dispatch]);
  return query;
};

export const useFetchItems = (itemIDs: number[], enabled?: boolean) => {
  const { dispatch } = useNuorderOrdering();
  const query = useQuery({
    queryKey: [QueryKeys.ITEMS, [...itemIDs].sort()],
    queryFn: () => fetchItems(itemIDs),
    enabled: useGetEnabled(enabled),
  });
  useEffect(() => {
    if (query.data) {
      dispatch({ type: NuorderOrderingActionTypes.ADD_ITEMS, payload: query.data });
    }
  }, [query.data, dispatch]);
  return query;
};

export const useFetchVendor = (vendorID: number, enabled?: boolean) => {
  const { dispatch } = useNuorderOrdering();
  const query = useQuery({
    queryKey: [QueryKeys.VENDOR, vendorID],
    queryFn: () => fetchVendor(vendorID),
    enabled: useGetEnabled(enabled),
  });
  useEffect(() => {
    if (query.data) {
      dispatch({ type: NuorderOrderingActionTypes.SET_VENDOR, payload: query.data });
    }
  }, [query.data, dispatch]);
  return query;
};

export const useFetchVendorShop = (vendorID: number, shopID: number, enabled?: boolean) => {
  const { dispatch } = useNuorderOrdering();
  const query = useQuery({
    queryKey: [QueryKeys.VENDOR_SHOP, vendorID, shopID],
    queryFn: () => fetchVendorShop(vendorID, shopID),
    enabled: useGetEnabled(enabled),
  });
  useEffect(() => {
    if (query.data) {
      dispatch({ type: NuorderOrderingActionTypes.SET_VENDOR_SHOP, payload: query.data });
    }
  }, [query.data, dispatch]);
  return query;
};

export const useFetchNuorderOrder = (
  b2bSellerUID: string,
  b2bOrderUID: string,
  enabled?: boolean
) => {
  const { dispatch } = useNuorderOrdering();
  const query = useQuery({
    queryKey: [QueryKeys.NUORDER_ORDER, b2bSellerUID, b2bOrderUID],
    queryFn: () => fetchNuorderOrder(b2bSellerUID, b2bOrderUID),
    enabled: useGetEnabled(enabled),
  });
  useEffect(() => {
    if (query.data) {
      dispatch({
        type: NuorderOrderingActionTypes.SET_NUORDER_ORDER,
        payload: query.data,
      });
    }
  }, [query.data, dispatch]);
  return query;
};

export const useFetchNuorderBrand = (b2bSellerUID: string, enabled?: boolean) => {
  const { dispatch } = useNuorderOrdering();
  const query = useQuery({
    queryKey: [QueryKeys.NUORDER_BRAND, b2bSellerUID],
    queryFn: () => fetchNuorderBrand(b2bSellerUID),
    enabled: useGetEnabled(enabled),
  });
  useEffect(() => {
    if (query.data) {
      dispatch({
        type: NuorderOrderingActionTypes.SET_NUORDER_BRAND,
        payload: query.data,
      });
    }
  }, [query.data, dispatch]);
  return query;
};

export const useFetchNuorderProducts = (
  b2bSellerUID: string,
  UPCs: string[],
  enabled?: boolean
) => {
  const { dispatch } = useNuorderOrdering();
  const query = useQuery({
    queryKey: [QueryKeys.NUORDER_PRODUCTS, [...UPCs].sort()],
    queryFn: () => fetchNuorderProductByUPC(b2bSellerUID, UPCs),
    enabled: useGetEnabled(enabled),
  });
  useEffect(() => {
    if (query.data) {
      dispatch({
        type: NuorderOrderingActionTypes.ADD_NUORDER_PRODUCTS,
        payload: query.data,
      });
    }
  }, [query.data, dispatch]);
  return query;
};

export const useFetchNuorderAvailability = (
  b2bSellerUID: string,
  uuids: string[],
  enabled?: boolean
) => {
  const { dispatch } = useNuorderOrdering();
  const query = useQuery({
    queryKey: [QueryKeys.NUORDER_AVAILABILITY, [...uuids].sort()],
    queryFn: () => fetchNuorderAvailability(b2bSellerUID, uuids),
    enabled: useGetEnabled(enabled),
  });
  useEffect(() => {
    if (query.data) {
      dispatch({
        type: NuorderOrderingActionTypes.ADD_NUORDER_PRODUCT_AVAILABILITY,
        payload: query.data,
      });
    }
  }, [query.data, dispatch]);
  return query;
};

export const useFetchConfigs = (enabled?: boolean) => {
  const { dispatch } = useNuorderOrdering();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: [QueryKeys.CONFIGS],
    queryFn: () => fetchConfigs(),
    enabled: useGetEnabled(enabled),
  });
  useEffect(() => {
    if (query.data) {
      dispatch({
        type: NuorderOrderingActionTypes.SET_CONFIGS,
        payload: query.data,
      });
      queryClient.setQueryData([QueryKeys.CONFIGS], query.data);
    }
  }, [query.data, dispatch, queryClient]);
  return query;
};

export const useFetchSortingPreference = (enabled?: boolean) => {
  const { dispatch } = useNuorderOrdering();
  const query = useQuery({
    queryKey: [QueryKeys.SORTING_PREFERENCE],
    queryFn: () => fetchSortingPreference(),
    enabled: useGetEnabled(enabled),
  });
  useEffect(() => {
    if (query.data) {
      dispatch({
        type: NuorderOrderingActionTypes.SET_SORTING_PREFERENCE,
        payload: query.data,
      });
    }
  }, [query.data, dispatch]);
  return query;
};

export const useUpdateSortingPreference = () => {
  const { dispatch } = useNuorderOrdering();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => {
      return updateSortingPreference(data);
    },
    onSuccess: (data) => {
      dispatch({
        type: NuorderOrderingActionTypes.SET_SORTING_PREFERENCE,
        payload: data,
      });
      queryClient.setQueryData([QueryKeys.SORTING_PREFERENCE], data);
    },
  });
};

export const useUpdatePurchase = (purchaseID: number) => {
  const { dispatch } = useNuorderOrdering();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<PurchaseRequest>) => {
      return updatePurchase(purchaseID, data);
    },
    onSuccess: (data) => {
      // Optimistically update the purchase in the context
      dispatch({
        type: NuorderOrderingActionTypes.UPDATE_PURCHASE,
        payload: data,
      });
      // We cannot use the PUT response to update the purchase in the cache
      // since it does not return the full purchase object.
      // So we need to invalidate the query which will refetch the purchase.
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.PURCHASE, purchaseID],
        exact: true,
      });
    },
  });
};

export const useUpdateItem = (itemID: number) => {
  const { dispatch } = useNuorderOrdering();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Item>) => {
      return updateItem(itemID, data);
    },
    onSuccess: (data) => {
      const queries = queryClient.getQueriesData<Item[]>([QueryKeys.ITEMS]);
      queries.forEach(([queryKey, oldData]) => {
        if (!(oldData ?? []).some((item) => item.itemID === data.itemID)) return;
        queryClient.setQueryData(queryKey, (oldData: Item[] | undefined) =>
          oldData ? oldData.map((item) => (item.itemID === data.itemID ? data : item)) : oldData
        );
      });
      dispatch({ type: NuorderOrderingActionTypes.ADD_ITEMS, payload: [data] });
    },
  });
};

export const useUpdateItemVendorNum = (itemID: number, vendorID: number) => {
  const { dispatch } = useNuorderOrdering();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateItemVendorNumRequest) => {
      return updateItemVendorNum(itemID, vendorID, data.value, data.b2bCatalogUUID);
    },
    onSuccess: (data: ItemVendorNum | null) => {
      if (!data) {
        dispatch({
          type: NuorderOrderingActionTypes.DELETE_ITEM_VENDOR_NUM,
          payload: { itemID, vendorID },
        });
      } else {
        dispatch({
          type: NuorderOrderingActionTypes.UPDATE_ITEM_VENDOR_NUM,
          payload: data,
        });
      }
      queryClient.setQueryData([QueryKeys.ITEM_VENDOR_NUM, [itemID, vendorID]], data);
      // update ITEMS caches
      const queries = queryClient.getQueriesData<Item[]>([QueryKeys.ITEMS]);
      queries.forEach(([queryKey, oldData]) => {
        if (!(oldData ?? []).some((item) => item.itemID === itemID)) return;
        queryClient.setQueryData(queryKey, (oldData: Item[] | undefined) => {
          return (
            oldData &&
            oldData.map((item) => {
              if (item.itemID !== itemID) return item;
              item.ItemVendorNums = item.ItemVendorNums.filter(
                (itemVendorNum) => itemVendorNum.vendorID !== vendorID
              );
              if (data) {
                item.ItemVendorNums.push(data);
              }
              return item;
            })
          );
        });
      });
    },
  });
};

export const useUpdatePurchaseLine = (purchaseID: number, purchaseLineID: number) => {
  const { dispatch } = useNuorderOrdering();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<PurchaseLine>) => {
      return updatePurchaseLine(purchaseLineID, data);
    },
    onSuccess: (data) => {
      dispatch({
        type: NuorderOrderingActionTypes.UPDATE_PURCHASE_LINE,
        payload: data,
      });
      queryClient.setQueryData(
        [QueryKeys.PURCHASE_LINES, purchaseID],
        (oldData: PurchaseLine[] | undefined) => {
          return oldData
            ? oldData.map((line) => (line.orderLineID === purchaseLineID ? data : line))
            : oldData;
        }
      );
      // Refetch the purchase to retrieve that latest computed values like total, tax, etc.
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.PURCHASE, purchaseID],
        exact: true,
      });
    },
  });
};

export const useDeletePurchaseLine = (purchaseID: number, purchaseLineID: number) => {
  const { dispatch } = useNuorderOrdering();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      return deletePurchaseLine(purchaseLineID);
    },
    onSuccess: () => {
      dispatch({
        type: NuorderOrderingActionTypes.DELETE_PURCHASE_LINE,
        payload: purchaseLineID,
      });
      queryClient.setQueryData(
        [QueryKeys.PURCHASE_LINES, purchaseID],
        (oldData: PurchaseLine[] | undefined) => {
          return oldData ? oldData.filter((line) => line.orderLineID !== purchaseLineID) : oldData;
        }
      );
      // Refetch the purchase to retrieve that latest computed values like total, tax, etc.
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.PURCHASE, purchaseID],
        exact: true,
      });
    },
  });
};

export const useCreateNuorderOrder = (b2bSellerUID: string) => {
  const { dispatch } = useNuorderOrdering();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: NuorderOrderRequest) => {
      return createNuorderOrder(b2bSellerUID, data);
    },
    onSuccess: (data) => {
      dispatch({
        type: NuorderOrderingActionTypes.SET_NUORDER_ORDER,
        payload: data,
      });
      queryClient.setQueryData([QueryKeys.NUORDER_ORDER, b2bSellerUID, data.order_id], data);
    },
  });
};

export const useUpdateNuorderOrder = (b2bSellerUID: string, b2bOrderUID: string) => {
  const { dispatch } = useNuorderOrdering();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: NuorderOrderRequest) => {
      return updateNuorderOrder(b2bSellerUID, b2bOrderUID, data);
    },
    onSuccess: (data) => {
      dispatch({
        type: NuorderOrderingActionTypes.SET_NUORDER_ORDER,
        payload: data,
      });
      queryClient.setQueryData([QueryKeys.NUORDER_ORDER, b2bSellerUID, b2bOrderUID], data);
    },
  });
};

export const useCompleteNuorderOrder = (b2bSellerUID: string, b2bOrderUID: string) => {
  const { dispatch } = useNuorderOrdering();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      return completeNuorderOrder(b2bSellerUID, b2bOrderUID);
    },
    onSuccess: (data) => {
      dispatch({
        type: NuorderOrderingActionTypes.SET_NUORDER_ORDER,
        payload: data,
      });
      queryClient.setQueryData([QueryKeys.NUORDER_ORDER, b2bSellerUID, b2bOrderUID], data);
    },
  });
};
